"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import { supabase } from "./supabase";
import { event as gtagEvent } from "./gtag";
import { parseGuestProfile } from "./guest-profile";
import { getGuestCareerStage, clearGuestCareerStage } from "./guestCareerStage";
import { notifyProfileChanged } from "./profileRevalidation";
import { getAuthReturnTo, clearAuthReturnTo } from "./auth-return";
import { getAuthErrorMessage } from "./auth-errors";

type AuthContextType = {
  user: User | null;
  loading: boolean;
  signInWithGoogle: (options?: {
    redirectTo?: string;
  }) => Promise<{ error: string | null }>;
  signInWithEmail: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signUpWithEmail: (
    email: string,
    password: string,
    redirectTo?: string
  ) => Promise<{ error: string | null; needsConfirmation?: boolean }>;
  signOut: () => Promise<{ error: string | null }>;
  resetPasswordForEmail: (
    email: string
  ) => Promise<{ error: string | null }>;
  updatePassword: (
    password: string
  ) => Promise<{ error: string | null }>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signInWithGoogle: async () => ({ error: null }),
  signInWithEmail: async () => ({ error: null }),
  signUpWithEmail: async () => ({ error: null }),
  signOut: async () => ({ error: null }),
  resetPasswordForEmail: async () => ({ error: null }),
  updatePassword: async () => ({ error: null }),
});

const API_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001/api/v1";

async function migrateGuestProfile(accessToken: string) {
  try {
    const parsed = parseGuestProfile(sessionStorage.getItem("guestProfile"));
    if (!parsed) return;

    const res = await fetch(`${API_URL}/users/me`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        // 志望業界は引き継がない。業界マスタ参照へ移り、ゲストが保持している旧値を
        // そのまま送れなくなった。ゲスト側に志望業界を保存する導線も既にない。
        profile: {
          job_type: parsed.jobType ?? null,
          experience_level: parsed.experienceLevel ?? null,
        },
      }),
    });

    if (res.ok) {
      sessionStorage.removeItem("guestProfile");
      sessionStorage.setItem("profileMigrated", "true");
    }
  } catch {
    // 引き継ぎ失敗時は sessionStorage を保持し、次回リトライ可能にする
  }
}

async function migrateGuestCareerStage(accessToken: string) {
  try {
    const stage = getGuestCareerStage();
    if (!stage) return;

    const res = await fetch(`${API_URL}/users/me`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        profile: {
          career_stage: stage,
        },
      }),
    });

    if (res.ok || res.status === 409) {
      // 200: 引き継ぎ成功。409: 既に区分が確定済みで変更不可。
      // いずれも一時保存は破棄し、リトライループを避ける。
      clearGuestCareerStage();
    }

    if (res.ok) {
      // 表示中の画面に取り直させる。新規登録直後は、この引き継ぎが
      // ヘッダーのバッジの取得より後に終わることがある。
      notifyProfileChanged();
    }
  } catch {
    // 引き継ぎ失敗時は sessionStorage を保持し、次回リトライ可能にする
  }
}

async function claimGuestAnswers(accessToken: string) {
  try {
    await fetch(`${API_URL}/answers/claim`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: "include",
    });
  } catch {
    // claim 失敗時もリダイレクトは実行する
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);

      // パスワードリカバリーフロー中は SIGNED_IN のリダイレクト処理をスキップする
      if (
        event === "SIGNED_IN" &&
        window.location.pathname === "/update-password"
      ) {
        return;
      }

      // ゲスト→ログイン時のプロフィール自動引き継ぎ + フィードバック紐づけ + リダイレクト
      if (event === "SIGNED_IN" && session?.access_token) {
        // 新規ユーザー判定: created_at が直近60秒以内なら signup イベントを送信する
        const user = session.user;
        if (user.created_at) {
          const createdAt = new Date(user.created_at).getTime();
          const now = Date.now();
          if (now - createdAt < 60_000) {
            const method = user.app_metadata?.provider ?? "email";
            gtagEvent("signup", { method });
          }
        }

        migrateGuestProfile(session.access_token);
        // 未ログイン時に選んだ新卒/中途区分をプロフィールへ引き継ぐ
        migrateGuestCareerStage(session.access_token);

        // フィードバック画面からの認証の場合: ゲストフィードバックを紐づけてリダイレクトする
        const returnTo = getAuthReturnTo();
        if (returnTo) {
          claimGuestAnswers(session.access_token).then(() => {
            clearAuthReturnTo();
            // Google OAuth の場合は既にフィードバックページに遷移しているためスキップする
            if (window.location.pathname !== returnTo) {
              router.push(returnTo);
            }
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async (
    options?: { redirectTo?: string }
  ): Promise<{ error: string | null }> => {
    const destination = options?.redirectTo ?? "/dashboard";
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(destination)}`,
      },
    });
    if (error) {
      return { error: getAuthErrorMessage(error.message) };
    }
    return { error: null };
  };

  const signInWithEmail = async (
    email: string,
    password: string
  ): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      return { error: getAuthErrorMessage(error.message) };
    }
    return { error: null };
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    redirectTo?: string
  ): Promise<{ error: string | null; needsConfirmation?: boolean }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: redirectTo ? { emailRedirectTo: redirectTo } : undefined,
    });
    if (error) {
      return { error: getAuthErrorMessage(error.message) };
    }
    // If user exists but session is null, email confirmation is required
    const needsConfirmation = !!data.user && !data.session;
    return { error: null, needsConfirmation };
  };

  const signOut = async (): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      return { error: getAuthErrorMessage(error.message) };
    }
    return { error: null };
  };

  const resetPasswordForEmail = async (
    email: string
  ): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?next=/update-password`,
    });
    if (error) {
      return { error: getAuthErrorMessage(error.message) };
    }
    return { error: null };
  };

  const updatePassword = async (
    password: string
  ): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      return { error: getAuthErrorMessage(error.message) };
    }
    return { error: null };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        signOut,
        resetPasswordForEmail,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
